import React, { useEffect, useState } from 'react';
import QRCode from 'qrcode';
import { AlertTriangle, Check, Clock, Copy, ExternalLink, FlaskConical, Loader2, RefreshCw, X } from 'lucide-react';
import { legeTestLinkAn, testLinkUrl } from '../../services/sifa/testlinkService';
import { supabase } from '../../services/supabaseClient';
import { describeError } from '../../services/errorMessage';
import { KOPIEREN_FEHLER, TEST_HINWEIS, beiAuthEreignis, gueltigBisText, holeTestLink } from './testLinkLogic';

/**
 * Einmal je Seite: Abmelden, geaenderte Nutzerdaten oder ein anderer Nutzer
 * leeren den Test-Link-Cache (beiAuthEreignis). Registriert beim ersten
 * Dialog, nicht schon beim Laden des Moduls.
 */
let authLauscherAktiv = false;
const lauscheAufAnmeldung = (): void => {
  if (authLauscherAktiv) return;
  authLauscherAktiv = true;
  try {
    supabase.auth.onAuthStateChange((ereignis: string, session: { user?: { id?: string } } | null) => {
      beiAuthEreignis(ereignis, session?.user?.id ?? null);
    });
  } catch {
    // Ohne Lauscher bleibt der Nutzer im Schluessel — der Cache trennt trotzdem nach Nutzer.
    authLauscherAktiv = false;
  }
};

/** Angemeldeter Nutzer aus der lokalen Sitzung — ohne ihn merkt holeTestLink nichts. */
const angemeldeterNutzer = async (): Promise<string | null> => {
  try {
    const { data } = await supabase.auth.getSession();
    const uid = data?.session?.user?.id;
    return typeof uid === 'string' && uid.trim() ? uid : null;
  } catch {
    return null;
  }
};

interface Props {
  art: 'unterweisung' | 'vorlage';
  /** id der Kunden-Unterweisung bzw. der Vorlage — getestet wird ihr GESPEICHERTER Stand. */
  id: string;
  titel: string;
  onSchliessen: () => void;
}

type Stand =
  | { art: 'laedt' }
  | { art: 'fertig'; url: string; gueltigBis: string; qr: string | null }
  | { art: 'fehler'; text: string };

/**
 * „Testen" (docs/sifa-ausbau2-plan.md §0.1): zeigt einen Test-Link (24 h,
 * Datenbank) als QR-Code fuers Handy, zum Kopieren und zum Oeffnen am PC. Im
 * Test wird nichts gespeichert.
 *
 * Der Link wird wiederverwendet, solange er noch eine Stunde gilt
 * (holeTestLink) — der Inhalt ist trotzdem immer der gerade gespeicherte,
 * denn die Datenbank liest ihn beim Aufruf. „Neuen Link erzeugen" legt einen
 * frischen an. Der QR-Code kommt aus derselben Bibliothek wie der des
 * Kundenlinks (UnterweisungListe); scheitert nur er, bleibt der Link nutzbar.
 */
export const TestLinkDialog: React.FC<Props> = ({ art, id, titel, onSchliessen }) => {
  const [stand, setStand] = useState<Stand>({ art: 'laedt' });
  /** Jede Anforderung zaehlt hoch und startet den Effekt neu; `neu` erzwingt einen frischen Link. */
  const [anforderung, setAnforderung] = useState<{ nr: number; neu: boolean }>({ nr: 0, neu: false });
  const [kopiert, setKopiert] = useState(false);
  const [kopierFehler, setKopierFehler] = useState<string | null>(null);

  useEffect(() => {
    let aktiv = true;
    (async () => {
      try {
        lauscheAufAnmeldung();
        const userId = await angemeldeterNutzer();
        const link = await holeTestLink(art, id, legeTestLinkAn, { userId, neu: anforderung.neu });
        const url = testLinkUrl(link.token);
        const qr = await QRCode.toDataURL(url, { width: 480, margin: 2 }).catch(() => null);
        if (aktiv) setStand({ art: 'fertig', url, gueltigBis: link.gueltigBis, qr });
      } catch (e) {
        if (aktiv) setStand({ art: 'fehler', text: describeError(e) });
      }
    })();
    return () => { aktiv = false; };
  }, [art, id, anforderung]);

  const anfordern = (neu: boolean) => {
    setStand({ art: 'laedt' });
    setKopiert(false);
    setKopierFehler(null);
    setAnforderung((a) => ({ nr: a.nr + 1, neu }));
  };

  const kopieren = async (url: string) => {
    setKopierFehler(null);
    try {
      await navigator.clipboard.writeText(url);
      setKopiert(true);
      window.setTimeout(() => setKopiert(false), 2000);
    } catch {
      // Zwischenablage gesperrt (unsicherer Kontext): der Link steht darunter zum Markieren.
      setKopiert(false);
      setKopierFehler(KOPIEREN_FEHLER);
    }
  };

  const knopf = 'min-h-[44px] px-4 flex items-center justify-center gap-2 text-sm font-bold rounded-xl transition-colors';

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      onClick={onSchliessen}
      onKeyDown={(e) => { if (e.key === 'Escape') onSchliessen(); }}
      role="presentation"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Unterweisung testen"
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-5 space-y-4 max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <h3 className="text-sm font-bold text-slate-800 flex items-center gap-1.5"><FlaskConical className="w-4 h-4 text-teal-600" /> Unterweisung testen</h3>
            <p className="text-xs text-slate-500 truncate">{titel || 'ohne Titel'}</p>
          </div>
          <button type="button" autoFocus onClick={onSchliessen} className="p-2 hover:bg-slate-100 rounded-full" aria-label="Schließen">
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>

        <div className="p-3 bg-amber-50 border border-amber-300 rounded-xl text-xs text-amber-900">
          {TEST_HINWEIS} Getestet wird der gespeicherte Stand.
        </div>

        {stand.art === 'laedt' && (
          <div className="p-8 flex flex-col items-center gap-2 text-xs text-slate-500" aria-live="polite">
            <Loader2 className="w-6 h-6 animate-spin text-teal-500" /> Test-Link wird vorbereitet …
          </div>
        )}

        {stand.art === 'fehler' && (
          <div className="space-y-3">
            <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-xs text-red-700 flex items-start gap-2" role="alert">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-px" /><span>{stand.text}</span>
            </div>
            <button type="button" onClick={() => anfordern(false)} className={`${knopf} w-full text-slate-700 bg-slate-100 hover:bg-slate-200`}>
              <RefreshCw className="w-4 h-4" /> Erneut versuchen
            </button>
          </div>
        )}

        {stand.art === 'fertig' && (
          <div className="grid grid-cols-1 sm:grid-cols-[200px_1fr] gap-4">
            <div className="space-y-1">
              {stand.qr ? (
                <img src={stand.qr} alt="QR-Code zum Test-Link" className="w-full max-w-[200px] mx-auto rounded-lg border border-slate-200" />
              ) : (
                <p className="text-xs text-slate-500 text-center">Der QR-Code ließ sich nicht erzeugen — der Link funktioniert trotzdem.</p>
              )}
              <p className="text-[11px] text-slate-500 text-center">Mit dem Handy scannen</p>
            </div>
            <div className="space-y-3">
              <a
                href={stand.url}
                target="_blank"
                rel="noopener noreferrer"
                className={`${knopf} w-full text-white bg-teal-600 hover:bg-teal-700`}
              >
                <ExternalLink className="w-4 h-4" /> Am PC öffnen
              </a>
              <button type="button" onClick={() => void kopieren(stand.url)} className={`${knopf} w-full text-slate-700 bg-white border border-slate-200 hover:bg-slate-50`}>
                {kopiert ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />} {kopiert ? 'Kopiert' : 'Link kopieren'}
              </button>
              {kopierFehler && (
                <p className="text-xs text-red-700 flex items-start gap-1.5" role="alert"><AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-px" /> {kopierFehler}</p>
              )}
              <p className="text-[10px] text-slate-400 break-all select-all" title="Test-Link">{stand.url}</p>
              {gueltigBisText(stand.gueltigBis) && (
                <p className="text-xs text-slate-600 flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> {gueltigBisText(stand.gueltigBis)}</p>
              )}
              <button type="button" onClick={() => anfordern(true)} className="text-xs font-semibold text-teal-700 hover:underline flex items-center gap-1">
                <RefreshCw className="w-3 h-3" /> Neuen Link erzeugen
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
